import { GetString } from 'fluent-react';
import React, { RefObject } from 'react';
import styled from 'styled-components';
import {
  CountryProfile,
  Location,
  PolicyRecommendation,
} from '../../graphQL/types/shared';
import { gql, useQuery } from '../../graphQL/useQuery';
import { SlideElementProps } from '../../presentationManager/PresentationManager';
import { failIfValidOrNonExhaustive } from '../../Utils';
import { getCountryName } from '../../Utils';
import {
  FlowChartButtonContainer,
  GraphAreaContainerExtendFull,
  GraphAreaContainerTopBar,
  searchBarHeight,
  searchBarHeightSmall,
} from '../Grid';
import { smallMediaQuery } from '../Grid';
import { rockSongGridColumnsCss } from '../rockSongChart/Grid';
import FlowChart from './FlowChart';
import ToggleButton from './ToggleButton';

const query = gql`
  query rockSongFlowChartQuery($countryId: ID!) {
    location(id: $countryId) {
      id
      shortName
      nameAbbr
      thePrefix
    }
    countryProfile(location: $countryId) {
      policyRecommendation
    }
  }
`;

const ButtonContainer = styled(FlowChartButtonContainer)`
  grid-row: 1;
  grid-column: 1;
  display: grid;
  height: ${searchBarHeight}rem;
  background-color: #fff;
  align-items: end;
  ${rockSongGridColumnsCss}

  @media ${smallMediaQuery} {
    height: ${searchBarHeightSmall}rem;
  }
`;

const ButtonRoot = styled(GraphAreaContainerTopBar)`
  display: grid;
  grid-template-rows: auto 1fr;
`;
const FlowChartRoot = styled(GraphAreaContainerExtendFull)`
  display: grid;
  grid-template-rows: auto 1fr;
`;

interface SuccessResponse {
  location: null | {
    id: Location['id']
    shortName: Location['shortName']
    nameAbbr: Location['nameAbbr']
    thePrefix: Location['thePrefix'],
  };
  countryProfile: null | {
    policyRecommendation: CountryProfile['policyRecommendation'],
  };
}

interface Variables {
  countryId: string;
}

interface Props extends SlideElementProps {
  zIndex: number;
  getFluentString: GetString;
  countryId: string;
  flowChartOpen: PolicyRecommendation | null;
  setFlowChartOpen: (value: PolicyRecommendation | null) => void;
}

const RockSongFlowChart = React.forwardRef((props: Props, rootElRef: RefObject<HTMLDivElement>) => {
  const {
    zIndex, getFluentString, countryId,
    flowChartOpen, setFlowChartOpen,
  } = props;

  const variables: Variables = {
    countryId,
  };

  const {loading, error, data} = useQuery<SuccessResponse, Variables>(query, {variables});
  let output: React.ReactElement<any> | null;
  if (loading === true) {
    output = null;
  } else if (error !== undefined) {
    console.error(error);
    output = null;
  } else if (data !== undefined) {
    const {location, countryProfile} = data;
    if (location === null || countryProfile === null) {
      output = null;
    } else {
      const { shortName, nameAbbr, thePrefix } = location;
      const { policyRecommendation } = countryProfile;
      const {nameSecondary} = getCountryName({shortName, nameAbbr, thePrefix: Boolean(thePrefix)});
      if (policyRecommendation === null) {
        output = null;
      } else {
        let policyRecommendationString: string;
        if (policyRecommendation === PolicyRecommendation.LightTouch) {
          policyRecommendationString = getFluentString('strategic-approach-name-light-touch');
        } else if (policyRecommendation === PolicyRecommendation.StrategicBets) {
          policyRecommendationString = getFluentString('strategic-approach-name-strategic-bets');
        } else if (policyRecommendation === PolicyRecommendation.ParsimoniousIndustrial) {
          policyRecommendationString = getFluentString('strategic-approach-name-parsimonious-industrial');
        } else if (policyRecommendation === PolicyRecommendation.TechFrontier) {
          policyRecommendationString = getFluentString('strategic-approach-name-tech-frontier');
        } else {
          failIfValidOrNonExhaustive(policyRecommendation,
            'Invalid policy recommendation ' + policyRecommendation);
          policyRecommendationString = '';
        }
        const countryName = flowChartOpen === policyRecommendation ? nameSecondary : 'the country';
        const flowChart = flowChartOpen === null ? null : (
            <FlowChart
              getFluentString={getFluentString}
              policyRecommendation={flowChartOpen}
              countryName={countryName}
            />
        );

        const showFlowChart = () => {
          setFlowChartOpen(policyRecommendation);
        };
        const hideFlowChart = () => {
          setFlowChartOpen(null);
        };
        output = (
          <>
            <ButtonContainer>
              <ToggleButton
                getFluentString={getFluentString}
                policyRecommendationString={policyRecommendationString}
                flowChartOpen={flowChartOpen === null ? false : true}
                showFlowChart={showFlowChart}
                hideFlowChart={hideFlowChart}
              />
            </ButtonContainer>
            {flowChart}
          </>
        );
      }
    }
  } else {
    output = null;
  }

  const Root = flowChartOpen !== null ? FlowChartRoot : ButtonRoot;

  return (
    <Root style={{zIndex}} ref={rootElRef}>
      {output}
    </Root>
  );
});

export default RockSongFlowChart;
